import { FLOOR_PAINT_COST } from '../config/balancing';
import { ENTRANCE, PATH_CELLS } from '../config/map';
import { isEntranceThreshold, isUnlockedPaintTargetCoord } from '../spatial/buildableArea';
import {
  connectedWalkableFrom,
  DEFAULT_SURFACE,
  isWalkable,
  subcellKey,
  visiblePathFloorCoverage,
  walkableCoverageFromLegacyCells,
  walkableKeySet,
  WALKABLE_SURFACE,
} from '../spatial/surfaces';
import type { FloorStyleId, ParkState, SubcellAnchor } from '../types';
import { entityOccupiedSubcells } from './placement';

/** Walkable path/plaza looks — shown first in the floor tray. */
export const FLOOR_PATH_STYLE_IDS: FloorStyleId[] = [
  'classic',
  'brick',
  'stone',
  'kidsTile',
  'wood',
  'festive',
];

/** Floral / garden-carpet looks — decorative painted ground, still walkable. */
export const FLOOR_FLORAL_STYLE_IDS: FloorStyleId[] = [
  'flowerPink',
  'flowerYellow',
  'flowerMix',
  'clover',
  'garden',
];

/** Every paintable (non-grass) floor style, in tray order. */
export const FLOOR_STYLE_IDS: FloorStyleId[] = [...FLOOR_PATH_STYLE_IDS, ...FLOOR_FLORAL_STYLE_IDS];
export type FloorPaintStyle = FloorStyleId;

export function isFloralFloorStyle(style: FloorStyleId): boolean {
  return FLOOR_FLORAL_STYLE_IDS.includes(style);
}

/** Coins charged to change one cell. Grass/erase and same-style repaint are free to charge. */
export function floorPaintCoinCost(current: FloorPaintStyle | null, next: FloorPaintStyle): number {
  if (next === 'grass' || current === next) {
    return 0;
  }
  return FLOOR_PAINT_COST;
}

/**
 * Unpaid ground: bare grass, or the canonical default-path classic that was never bought.
 * An explicit `state.floors` override is the current-paid marker — including paid classic.
 * No ledger — only the current visible style plus that override flag is considered.
 */
export function isUnpaidDefaultFloor(
  coord: SubcellAnchor,
  current: FloorPaintStyle | null,
  explicitOverride = false,
): boolean {
  if (!current || current === 'grass') {
    return true;
  }
  if (explicitOverride) {
    return false;
  }
  return defaultPathKeySet().has(subcellKey(coord)) && current === 'classic';
}

export function isExplicitFloorOverride(state: ParkState, coord: SubcellAnchor): boolean {
  return floorOverrideMap(state).has(subcellKey(coord));
}

/** Erasing a currently paid floor/flower cell refunds exactly one tile price. */
export function floorEraseRefundCoins(
  coord: SubcellAnchor,
  current: FloorPaintStyle | null,
  next: FloorPaintStyle,
  explicitOverride = false,
): number {
  if (next !== 'grass' || isUnpaidDefaultFloor(coord, current, explicitOverride)) {
    return 0;
  }
  return FLOOR_PAINT_COST;
}

export function defaultPathCoords(): SubcellAnchor[] {
  return visiblePathFloorCoverage(ENTRANCE, PATH_CELLS).map((cell) => cell.coord);
}

export function defaultPathKeySet(): Set<string> {
  return walkableKeySet(defaultPathCoords());
}

/** Full 5x5 of the central path/entrance corridor — includes 3-wide paving plus shoulders. */
export function pathCorridorCoords(): SubcellAnchor[] {
  return walkableCoverageFromLegacyCells([ENTRANCE, ...PATH_CELLS]);
}

export function pathCorridorKeySet(): Set<string> {
  return walkableKeySet(pathCorridorCoords());
}

export function isUnlockedPaintTarget(state: ParkState, coord: SubcellAnchor): boolean {
  if (pathCorridorKeySet().has(subcellKey(coord)) || isEntranceThreshold(coord)) {
    return true;
  }
  return isUnlockedPaintTargetCoord(coord, state.unlockedLandIds);
}

export function floorOverrideMap(state: ParkState): Map<string, FloorPaintStyle> {
  const map = new Map<string, FloorPaintStyle>();
  for (const cell of state.floors ?? []) {
    map.set(subcellKey(cell), cell.style);
  }
  return map;
}

export function resolvedFloorStyle(coord: SubcellAnchor, overrides: ReadonlyMap<string, FloorPaintStyle>): FloorPaintStyle | null {
  const key = subcellKey(coord);
  const painted = overrides.get(key);
  if (painted) {
    return painted;
  }
  return defaultPathKeySet().has(key) ? 'classic' : null;
}

export function walkableCoords(state: ParkState): SubcellAnchor[] {
  const overrides = floorOverrideMap(state);
  const keys = new Set<string>();
  const coords: SubcellAnchor[] = [];
  const consider = (coord: SubcellAnchor) => {
    const style = resolvedFloorStyle(coord, overrides);
    if (!style || style === 'grass') {
      return;
    }
    const key = subcellKey(coord);
    if (keys.has(key)) {
      return;
    }
    keys.add(key);
    coords.push(coord);
  };
  for (const coord of defaultPathCoords()) {
    consider(coord);
  }
  for (const cell of state.floors ?? []) {
    consider(cell);
  }
  const blocked = new Set<string>();
  for (const entity of state.entities) {
    for (const coord of entityOccupiedSubcells(entity)) {
      blocked.add(subcellKey(coord));
    }
  }
  return coords.filter((coord) => !blocked.has(subcellKey(coord)));
}

function entrancePavingCoords(): SubcellAnchor[] {
  return visiblePathFloorCoverage(ENTRANCE, PATH_CELLS)
    .filter((cell) => cell.role === 'entrance')
    .map((cell) => cell.coord);
}

export function entranceWalkable(state: ParkState): SubcellAnchor[] {
  const keys = walkableKeySet(walkableCoords(state));
  const start = entrancePavingCoords().find((coord) => isWalkable(coord, keys));
  if (!start) {
    return [];
  }
  return connectedWalkableFrom(start, keys);
}

export function paintFloor(state: ParkState, coord: SubcellAnchor, style: FloorPaintStyle): { ok: true } | { ok: false; reason: 'locked' } {
  if (!isUnlockedPaintTarget(state, coord)) {
    return { ok: false, reason: 'locked' };
  }
  const key = subcellKey(coord);
  const floors = state.floors ? [...state.floors] : [];
  const index = floors.findIndex((cell) => subcellKey(cell) === key);
  const isDefault = defaultPathKeySet().has(key);
  if (style === 'grass') {
    if (isEntranceThreshold(coord)) {
      if (index >= 0) {
        floors.splice(index, 1);
      }
    } else if (isDefault) {
      if (index >= 0) {
        floors[index] = { sx: coord.sx, sz: coord.sz, style: 'grass' };
      } else {
        floors.push({ sx: coord.sx, sz: coord.sz, style: 'grass' });
      }
    } else if (index >= 0) {
      floors.splice(index, 1);
    }
  } else if (index >= 0) {
    floors[index] = { sx: coord.sx, sz: coord.sz, style };
  } else if (!(style === 'classic' && isDefault)) {
    floors.push({ sx: coord.sx, sz: coord.sz, style });
  }
  state.floors = floors.length > 0 ? floors : undefined;
  return { ok: true };
}

export { DEFAULT_SURFACE, WALKABLE_SURFACE };
